import { api } from "@/lib/api/axios";
import {
  IContract,
  ICreateContract,
  IUpdateContract,
  IContractResponse,
  IApprovalDecisionRequest,
  IContractApproval,
  IUploadContractResponse
} from "@/interfaces/contracts.interface";
import { ApiResponse } from "@/interfaces/api-response.interface";

const CONTRATOS_API_PATH = '/staff';

export const contratosService = {
  /**
   * Create new contract
   * POST /staff/add-member
   */
  createContract: async (payload: ICreateContract): Promise<IContractResponse> => {
    return api.post<IContractResponse>(`${CONTRATOS_API_PATH}/add-member`, payload);
  },

  /**
   * Get all contracts for a movie
   * GET /staff/movie/:movieId
   */
  getContractsByMovie: async (movieId: number) => {
    const res = await api.get<ApiResponse<IContract[]>>(`${CONTRATOS_API_PATH}/movie/${movieId}`)
    return res;
  },

  /**
   * Get single contract by ID
   * GET /staff/:id
   */
  getContractById: async (id: number) => {
    return api.get<ApiResponse<IContract>>(`${CONTRATOS_API_PATH}/${id}`);
  },

  /**
   * Update contract (DRAFT only)
   * PATCH /staff/:id
   */
  updateContract: async (payload: IUpdateContract): Promise<IContractResponse> => {
    const { id, ...contractData } = payload;
    return api.patch<IContractResponse>(`${CONTRATOS_API_PATH}/${id}`, contractData);
  },

  /**
   * Soft delete contract (DRAFT only)
   * DELETE /staff/:id
   */
  deleteContract: async (id: number): Promise<{ message: string }> => {
    return api.delete<{ message: string }>(`${CONTRATOS_API_PATH}/${id}`);
  },

  /**
   * Submit contract for review
   * POST /staff/:id/submit
   */
  submitContract: async (id: number): Promise<IContractResponse> => {
    return api.post<IContractResponse>(`${CONTRATOS_API_PATH}/${id}/submit`);
  },

  /**
   * Process approval decision (approve/reject/request changes)
   * POST /staff/:id/approve
   */
  approveContract: async (
    id: number,
    decision: IApprovalDecisionRequest
  ) => {
    return api.post<IContractResponse>(`${CONTRATOS_API_PATH}/${id}/approve`, decision);
  },

  /**
   * Contractor accepts contract
   * POST /staff/:id/accept
   */
  acceptContract: async (id: number) => {
    return api.post<IContractResponse>(`${CONTRATOS_API_PATH}/${id}/accept`);
  },

  /**
   * Activate signed contract (triggers blockchain)
   * POST /staff/:id/activate
   */
  activateContract: async (id: number) => {
    return api.post<IContractResponse>(`${CONTRATOS_API_PATH}/${id}/activate`);
  },

  /**
   * Get approval history
   * GET /staff/:id/approvals
   */
  getApprovalHistory: async (id: number) => {
    return api.get<IContractApproval[]>(`${CONTRATOS_API_PATH}/${id}/approvals`);
  },

  /**
   * Upload contract document (PDF)
   * POST /staff/upload-contract
   */
  uploadContractDocument: async (formData: FormData) => {
    return api.post<IUploadContractResponse>(
      `${CONTRATOS_API_PATH}/upload-contract`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  }
};
